/**
 * One level as a diorama (05 §5): the blocks, the back wall behind them, and the key light
 * whose shadows tie the two together. Built once per level; the drop between levels shows
 * two of these at once, one above the other.
 */

import {
  DirectionalLight, Group, HemisphereLight, type Material, Mesh, type Texture,
} from 'three';
import type { TileGrid } from '../../levels/grid';
import { createToonMaterial, OUTLINE } from '../materials/toon';
import type { Grade, Palette } from '../palette';
import { buildBackdrop } from './backdrop';
import { type BlockStyle, buildBlocks } from './blocks';

export interface StageOptions {
  grid: TileGrid;
  style: BlockStyle;
  palette: Palette;
  gradientMap: Texture;
  seed: number;
}

export class StageView {
  readonly group = new Group();
  private readonly materials: Material[] = [];
  private readonly meshes: Mesh[] = [];

  constructor(opts: StageOptions) {
    const blockMat = createToonMaterial({ gradientMap: opts.gradientMap, outline: OUTLINE.block });
    const wallMat = createToonMaterial({ gradientMap: opts.gradientMap, outline: OUTLINE.none });
    this.materials.push(blockMat, wallMat);

    const blocks = new Mesh(buildBlocks(opts.grid, opts.style, opts.palette).build(), blockMat);
    blocks.castShadow = true;
    blocks.receiveShadow = true;
    const wall = new Mesh(buildBackdrop(opts.grid, opts.palette, opts.seed).build(), wallMat);
    wall.receiveShadow = true;
    this.meshes.push(blocks, wall);
    this.group.add(blocks, wall);
  }

  dispose(): void {
    for (const m of this.meshes) m.geometry.dispose();
    for (const m of this.materials) m.dispose();
  }
}

/** The key light and the fill (05 §7), fitted to a stage `width` x `height` tiles centred on the origin. */
export function createStageLights(grade: Grade, width: number, height: number): Group {
  const group = new Group();
  const hemi = new HemisphereLight(grade.skyColor, grade.groundColor, grade.hemiIntensity);
  const sun = new DirectionalLight(grade.sunColor, grade.sunIntensity);
  const [dx, dy, dz] = grade.sunDir;
  const dist = 40;
  sun.position.set(-dx * dist, -dy * dist, -dz * dist);
  sun.target.position.set(0, 0, 0);
  sun.castShadow = true;
  sun.shadow.mapSize.set(2048, 2048);
  const half = Math.max(width, height) / 2 + 6;
  const cam = sun.shadow.camera;
  cam.left = -half;
  cam.right = half;
  cam.top = half;
  cam.bottom = -half;
  cam.near = 1;
  cam.far = dist * 2;
  sun.shadow.bias = -0.0008;
  sun.shadow.normalBias = 0.02;
  group.add(hemi, sun, sun.target);
  return group;
}
